// Task Nomenclature Service
// Generic CRUD for all 18 task-related nomenclature types

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// Nomenclature collection names
export const NOMENCLATURE_TYPES = {
  TASK_TYPES: 'task_types',
  TASK_STATUSES: 'task_statuses',
  TASK_PRIORITIES: 'task_priorities',
  TASK_URGENCIES: 'task_urgencies',
  ASSIGNMENT_TYPES: 'assignment_types',
  RECURRENCE_FREQUENCIES: 'recurrence_frequencies',
  PATIENT_CONDITIONS: 'patient_conditions',
  ISOLATION_LEVELS: 'isolation_levels',
  EQUIPMENT_TYPES_TASK: 'equipment_types_task',
  PROCEDURE_TYPES: 'procedure_types',
  ENVIRONMENTAL_RISKS: 'environmental_risks',
  SHIFT_TYPES: 'shift_types',
  COMPLETION_QUALITIES: 'completion_qualities',
  DEVIATION_TYPES: 'deviation_types',
  RISK_LEVELS: 'risk_levels',
  GROUP_TYPES: 'group_types',
  WORKLOAD_BALANCING_METHODS: 'workload_balancing_methods',
  NOTIFICATION_CHANNELS: 'notification_channels',
  TRIGGER_EVENTS: 'trigger_events'
};

export class TaskNomenclatureService {
  constructor() {
    this.collections = {};
    
    // Initialize collection references
    Object.values(NOMENCLATURE_TYPES).forEach(collectionName => {
      this.collections[collectionName] = collection(db, collectionName);
    });
  }
  
  // ==================== GENERIC CRUD ====================
  
  /**
   * Get all items from a nomenclature collection
   */
  async getAll(nomenclatureType) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const q = query(
        collectionRef,
        where('isActive', '==', true),
        orderBy('displayOrder', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Get all items including inactive
   */
  async getAllIncludingInactive(nomenclatureType) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const q = query(collectionRef, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching all ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Get single item by ID
   */
  async getById(nomenclatureType, id) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const docRef = doc(collectionRef, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching ${nomenclatureType} by ID:`, error);
      throw error;
    }
  }
  
  /**
   * Create new nomenclature item
   */
  async create(nomenclatureType, data, userId) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const now = Timestamp.now();
      
      // Get next display order
      const allItems = await this.getAllIncludingInactive(nomenclatureType);
      const maxOrder = allItems.length > 0 
        ? Math.max(...allItems.map(item => item.displayOrder || 0))
        : 0;
      
      const nomenclatureData = {
        ...data,
        displayOrder: maxOrder + 1,
        isActive: data.isActive !== undefined ? data.isActive : true,
        createdAt: now,
        createdBy: userId,
        updatedAt: now,
        updatedBy: userId
      };
      
      const docRef = await addDoc(collectionRef, nomenclatureData);
      return docRef.id;
    } catch (error) {
      console.error(`Error creating ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Update nomenclature item
   */
  async update(nomenclatureType, id, updates, userId) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const docRef = doc(collectionRef, id);
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId
      });
    } catch (error) {
      console.error(`Error updating ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete nomenclature item (soft delete)
   */
  async delete(nomenclatureType, id, userId) {
    try {
      await this.update(nomenclatureType, id, { isActive: false }, userId);
    } catch (error) {
      console.error(`Error deleting ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Hard delete nomenclature item
   */
  async hardDelete(nomenclatureType, id) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const docRef = doc(collectionRef, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error hard deleting ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Reorder nomenclature items
   */
  async reorder(nomenclatureType, orderedIds, userId) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      orderedIds.forEach((id, index) => {
        const docRef = doc(collectionRef, id);
        batch.update(docRef, {
          displayOrder: index + 1,
          updatedAt: now,
          updatedBy: userId
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error(`Error reordering ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  // ==================== BULK OPERATIONS ====================
  
  /**
   * Initialize default data for a nomenclature type
   */
  async initializeDefaults(nomenclatureType, defaultData, userId) {
    try {
      const collectionRef = this.collections[nomenclatureType];
      if (!collectionRef) {
        throw new Error(`Invalid nomenclature type: ${nomenclatureType}`);
      }
      
      // Check if data already exists
      const existing = await this.getAllIncludingInactive(nomenclatureType);
      if (existing.length > 0) {
        throw new Error(`${nomenclatureType} already has data. Clear first or use update.`);
      }
      
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      defaultData.forEach((item, index) => {
        const docRef = doc(collectionRef);
        batch.set(docRef, {
          ...item,
          displayOrder: index + 1,
          isActive: true,
          createdAt: now,
          createdBy: userId,
          updatedAt: now,
          updatedBy: userId
        });
      });
      
      await batch.commit();
      console.log(`✅ Initialized ${defaultData.length} items for ${nomenclatureType}`);
    } catch (error) {
      console.error(`Error initializing ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Clear all data from a nomenclature type
   */
  async clearAll(nomenclatureType) {
    try {
      const allItems = await this.getAllIncludingInactive(nomenclatureType);
      
      const batch = writeBatch(db);
      allItems.forEach(item => {
        const docRef = doc(this.collections[nomenclatureType], item.id);
        batch.delete(docRef);
      });
      
      await batch.commit();
      console.log(`✅ Cleared ${allItems.length} items from ${nomenclatureType}`);
    } catch (error) {
      console.error(`Error clearing ${nomenclatureType}:`, error);
      throw error;
    }
  }
  
  /**
   * Initialize all task nomenclatures with default data
   */
  async initializeAllDefaults(userId) {
    try {
      console.log('🚀 Initializing all task nomenclatures...');
      
      // Import default data
      const defaultData = await import('./defaultTaskNomenclatures');
      
      // Initialize each nomenclature type
      for (const [type, data] of Object.entries(defaultData.default)) {
        try {
          await this.initializeDefaults(type, data, userId);
        } catch (error) {
          console.warn(`⚠️ Skipping ${type}:`, error.message);
        }
      }
      
      console.log('✅ All task nomenclatures initialized');
    } catch (error) {
      console.error('Error initializing all defaults:', error);
      throw error;
    }
  }
}

export const taskNomenclatureService = new TaskNomenclatureService();


